import React, { memo, useEffect, useMemo, useState } from 'react';
import { Tooltip, Divider, Flex } from 'antd';
import { ChangelogIcon } from '@/assets/icons/icons.jsx';
import CustomParagraph from '@/components/CustomParagraph';
import { GetCourierChangelog } from '@/apis/settings';
import { useUnAuthorize } from '@/hooks';
import { useToken } from '@/common/helper';
import { useSpin } from '@/components/SpinContext';

interface ChangelogEntry {
  updated_by_name: string;
  updated_by_title: string;
  formatted_updated_at: string;
}

type Props = {
  providerData?: { id?: string };
};

const truncateByLength = (val?: string, max = 12) =>
  (val ?? '--').length > max
    ? `${(val ?? '--').slice(0, max)}…`
    : (val ?? '--');

/**
 * Displays the formatted changelog entries within the tooltip.
 *
 * @param changelog - Array of changelog entries with user info and timestamps
 */
const ChangelogContent: React.FC<{ changelog?: ChangelogEntry[] }> = ({
  changelog,
}) => (
  <>
    <CustomParagraph
      text='Changelog'
      noText='--'
      fontSize={'12'}
      lineHeight='16px'
      fontWeight={600}
      color='var(--text-secondary)'
      marginBottom={0}
    />
    <Divider style={{ margin: '8px 0' }} />

    {(changelog ?? []).map((entry, index) => (
      <Flex
        key={index}
        gap={8}
        align='center'
        justify='space-between'
        style={{ marginBottom: '4px' }}
      >
        <Flex gap={4} align='center'>
          <CustomParagraph
            text={`${truncateByLength(entry?.updated_by_name, 12)},`}
            noText='--'
            fontSize={'10'}
            lineHeight='16px'
            fontWeight={600}
            color='var(--text-primary)'
            marginBottom={0}
          />
          <CustomParagraph
            text={truncateByLength(entry?.updated_by_title, 16)}
            noText='--'
            fontSize={'10'}
            lineHeight='16px'
            fontWeight={400}
            color='var(--text-primary)'
            marginBottom={0}
          />
        </Flex>

        <CustomParagraph
          text={entry?.formatted_updated_at}
          noText='--'
          fontSize={'10'}
          lineHeight='16px'
          fontWeight={400}
          color='var(--text-secondary)'
          marginBottom={0}
        />
      </Flex>
    ))}
  </>
);

/**
 * Tooltip component that displays changelog history for a courier provider.
 * Fetches and displays a list of updates with user information and timestamps.
 *
 * @param providerData - Provider data object containing the provider ID
 */
const ChangelogTooltip: React.FC<Props> = memo(({ providerData }) => {
  const { showSpin, hideSpin } = useSpin();
  const { token } = useToken();
  const { unauthorize } = useUnAuthorize();

  const [changelog, setChangeLog] = useState<ChangelogEntry[]>([]);

  useEffect(() => {
    if (!providerData?.id) return;

    let isMounted = true;
    showSpin();
    GetCourierChangelog(token, providerData.id)
      .then((res) => {
        const results = res?.data?.results ?? [];

        const items: ChangelogEntry[] = results.map((r): ChangelogEntry => ({
          updated_by_name: r?.user_info?.full_name ?? 'System',
          updated_by_title: r?.user_info?.title ?? '',
          formatted_updated_at: r?.formatted_timestamp ?? '',
        }));

        if (isMounted) setChangeLog(items);
      })
      .catch((err) => {
        unauthorize(err);
      })
      .finally(() => {
        if (isMounted) hideSpin();
      });

    return () => {
      isMounted = false;
    };
  }, [providerData?.id, showSpin, hideSpin, token, unauthorize]);

  const tooltipContent = useMemo(
    () => <ChangelogContent changelog={changelog} />,
    [changelog]
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement='bottom'
      overlayClassName='changelog-tooltip-overlay'
      overlayStyle={{ minWidth: 296 }}
      trigger='hover'
    >
      <span title='Changelog' style={{ cursor: 'pointer', height: 16 }}>
        <ChangelogIcon />
      </span>
    </Tooltip>
  );
});

ChangelogTooltip.displayName = 'ChangelogTooltip';

export default ChangelogTooltip;
